import { AppContext } from 'App.context';
import { useContext, useEffect, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import FIELDS from '../../../civitfun-police-report-utils/PoliceReportFields';
import PoliceReportConstants from '../../../civitfun-police-report-utils/police.constants';
import {
  createReportToApiUpdate,
  getMunicipalityByPostalCode,
} from '../../../civitfun-police-report-utils/PoliceReportParse.utils';
// import { isValidPostalCode } from '../../../civitfun-police-report-utils/validations/validatePostalCode';
import PoliceReportValidations from '../../../civitfun-police-report-utils/validations/PoliceReportValidations';
import moment from 'moment';
import CONSTANTS from '../../../../../../utils/constants';
const {
  MUNICIPALITIES,
  PAYMENT_TYPE,
  ROLE_TYPE,
  CONTACT_TYPE,
  DOCUMENT_TYPE,
  FAMILY_RELATIONSHIP,
  COUNTRIES,
  ADULT_AGE,
} = PoliceReportConstants;

const { isMandatoryFieldEmpty, getFieldValidations } = PoliceReportValidations;

export default function usePoliceReportDetailHook(initialReport) {
  const {
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);
  const navigate = useNavigate();

  const { API } = useContext(ApiContext);

  const [state, setState] = useState({
    bookingId: initialReport?.bookingUUID,
    report: {},
    showSendWarning: false,
    showBookingDeleteWarning: false,
    showGuestDeleteWarning: false,
    disableForm: false,
    guestToDelete: null,
    errorFields: {
      contract: [],
      guests: Array.from({ length: initialReport?.numGuests }, () => []),
    },
    checkinCount: initialReport[FIELDS.NUMGUESTS_KEY],
  });
  /*
  // 
   state.report.contract.platform CONTAINS the platform  
  //
   */

  useEffect(() => {
    window.scrollTo(0, 0);
    actions.getPoliceReportDetail();
    if (initialReport?.platform === CONSTANTS.POLICE_REPORT_PLATFORMS.SES) {
      if (initialReport.status !== 'error') {
        updateState({ disableForm: true });
      }
    }
  }, []);

  const actions = {
    createOptionsFromType: (typeArray, translationPrefix) =>
      typeArray.map(type => ({
        value: translationPrefix ? type : type.value,
        label: translationPrefix
          ? translate(`policeReport.${translationPrefix}.${type}`)
          : type.label,
      })),

    getPaymentTypeOptions: () => actions.createOptionsFromType(PAYMENT_TYPE, 'paymentType'),
    getRoleTypeOptions: () => actions.createOptionsFromType(ROLE_TYPE, 'role'),
    getContactTypeOptions: () => actions.createOptionsFromType(CONTACT_TYPE, 'contactType'),
    getDocumentTypeOptions: () => actions.createOptionsFromType(DOCUMENT_TYPE, 'documentType'),
    getMunicipalityOptions: () => actions.createOptionsFromType(MUNICIPALITIES),
    getFamilyRelationshipOptions: () => actions.createOptionsFromType(FAMILY_RELATIONSHIP),
    getCountryOptions: () => actions.createOptionsFromType(COUNTRIES),
    disableSupportNumber: () => actions.createOptionsFromType(),

    onSendClicked: () => {
      actions.setShowSendWarning(true);
    },
    onDeleteBookingClicked: () => {
      actions.setShowBookingDeleteWarning(true);
    },
    onDeleteGuestClicked: guest => {
      updateState({ guestToDelete: guest });

      actions.setShowGuestDeleteWarning(true);
    },

    validateForm: ({ showErrorMsg, report }) => {
      const reportRef = report;
      const errors = {
        contract: [],
        guests: Array.from({ length: report.guests.length }, () => []),
      };

      const fieldValidations = getFieldValidations(reportRef, translate);

      for (const [_, field] of Object.entries(FIELDS)) {
        // validate by field section & guest
        if (
          [
            FIELDS.REFERENCE_KEY,
            FIELDS.CONTRACTDATE_KEY,
            FIELDS.ENTRANCE_KEY,
            FIELDS.DEPARTURE_KEY,
            FIELDS.NUMGUESTS_KEY,
            FIELDS.PAXES_KEY,
            FIELDS.ADULTS_KEY,
            FIELDS.CHILDREN_KEY,
            FIELDS.PAYMENTTYPE_KEY,
          ].includes(field)
        ) {
          if (isMandatoryFieldEmpty(reportRef.contract, field)) {
            errors.contract.push(field);
          }
        } else {
          for (let index = 0; index < reportRef.guests.length; index++) {
            const guest = reportRef.guests[index];
            if (isMandatoryFieldEmpty(guest, field)) {
              errors.guests[index].push(field);
            }
          }
        }

        if (field in fieldValidations) {
          // validate by field section & guest
          if (
            [
              FIELDS.REFERENCE_KEY,
              FIELDS.CONTRACTDATE_KEY,
              FIELDS.ENTRANCE_KEY,
              FIELDS.DEPARTURE_KEY,
              FIELDS.NUMGUESTS_KEY,
              FIELDS.PAXES_KEY,
              FIELDS.ADULTS_KEY,
              FIELDS.CHILDREN_KEY,
              FIELDS.PAYMENTTYPE_KEY,
            ].includes(field)
          ) {
            if (!fieldValidations[field].validate() && !errors.contract.includes(field)) {
              errors.contract.push(field);
              notification.error({
                message: fieldValidations[field].error,
              });
            }
          } else {
            for (let index = 0; index < reportRef.guests.length; index++) {
              const guest = reportRef.guests[index];
              if (
                !fieldValidations[field].validate(guest) &&
                !errors.guests[index].includes(field)
              ) {
                errors.guests[index].push(field);
                notification.error({
                  message: fieldValidations[field].error,
                });
              }
            }
          }
        }
      }

      showErrorMsg &&
        hasErrors() &&
        notification.error({
          message: translate(`policeReport.mandatoryFieldsError`),
        });

      updateState({ showSendWarning: false, errorFields: errors });
      return hasErrors() <= 0;

      function hasErrors() {
        const contractHasErrors = errors.contract && errors.contract.length > 0;
        const guestsHasErrors =
          errors.guests && errors.guests.some(guestErrors => guestErrors.length > 0);
        return contractHasErrors || guestsHasErrors;
      }
    },

    getPoliceReportDetail: () => {
      updateState({ isLoading: true });
      API.getPoliceReportDetail({
        hotelId: getSelectedHotel().key || getSelectedHotel().id,
        bookingId: state.bookingId,
      })
        .then(res => {
          res.guests.sort((a, b) => {
            if (a.holder && !b.holder) return -1;
            if (!a.holder && b.holder) return 1;

            if (a.roleType === 'TI' && b.roleType !== 'TI') {
              return -1;
            }
            if (a.roleType !== 'TI' && b.roleType === 'TI') {
              return 1;
            }
            return 0;
          });

          if (initialReport.status == 'error') {
            actions.validateForm({ showErrorMsg: false, report: res });
          }
          updateState({ isLoading: false, report: res });
        })
        .catch(err => {
          console.error(err);

          updateState({ isLoading: false });
        });
    },

    sendReport: () => {
      let errorCount = 0;
      if (actions.validateDocumentNumberRepeated()) {
        notification.error({
          message: translate('policeReport.error.documentDuplicated'),
        });
        updateState({ isLoading: false, disableForm: false, showSendWarning: false });
        return;
      }

      const isFormValid = actions.validateForm({ showErrorMsg: true, report: state.report });
      if (!isFormValid) {
        updateState({ isLoading: false, disableForm: false, showSendWarning: false });
        return;
      }

      state.report.guests.forEach(guest => {
        const sendEmptyDocument = actions.sendWithEmptyDocument(guest);
        if (sendEmptyDocument) {
          guest.documentType = '';
          guest.documentNumber = '';
          if (guest?.document_support_number) {
            guest.document_support_number = ''; // Clear the document_support_number field
            updateState(state);
          }
          if (guest?.supportDocument) {
            guest.supportDocument = ''; // Clear the supportDocument field
            updateState(state);
          }
        } else if (guest?.documentType === 'empty') {
          errorCount++;
          notification.error({
            message: translate('policeReport.error.documentRequired'),
          });
          return;
        }

        // Check if the guest has a support number (document_support_number or supportDocument)
        const hasSupportNumber = guest?.document_support_number || guest?.supportDocument;
        // Check if the document is a Spanish DNI or NIE
        const isDNIorNIE =
          guest?.documentType === 'dni' || guest?.documentType === 'residencePermit';

        // If the guest has a support number and it's NOT a Spanish DNI/NIE
        if (hasSupportNumber && !isDNIorNIE) {
          if (guest?.document_support_number) {
            guest.document_support_number = ''; // Clear the document_support_number field
            updateState(state);
          }
          if (guest?.supportDocument) {
            guest.supportDocument = ''; // Clear the supportDocument field
            updateState(state);
          }
        }
      });

      if (errorCount !== 0) {
        updateState({ isLoading: false, disableForm: false, showSendWarning: false });
        return;
      }

      updateState({ isLoading: true, disableForm: true, showSendWarning: false });

      // Register action
      API.registerDashboardAction('police_report', 'send_report', {
        hotelId: getSelectedHotel().key || getSelectedHotel().id,
        report: state.report,
      });

      API.postSendPoliceReport({
        data: createReportToApiUpdate(
          state.report,
          getSelectedHotel().key || getSelectedHotel().id,
          initialReport?.platform
        ),
      })
        .then(_ => {
          notification.success({
            message: translate('policeReport.edit.success'),
          });
          actions.setShowBookingDeleteWarning(false);
          updateState({ isLoading: false, disableForm: true });
        })
        .catch(_ => {
          notification.error({
            message: translate('policeReport.edit.error'),
          });
          updateState({ isLoading: false, disableForm: false });
        });
    },

    setShowSendWarning: show => {
      updateState({ showSendWarning: show });
    },
    setShowBookingDeleteWarning: show => {
      updateState({ showBookingDeleteWarning: show });
    },
    setShowGuestDeleteWarning: show => {
      updateState({ showGuestDeleteWarning: show });
    },

    showFamilyRelationship: birthDate => {
      // return state.report[FIELDS.CHILDREN_KEY] > 0;
      return actions.getIsAdult(birthDate);
    },

    getIsAdult: birthDate => {
      let birthDateFormatted = moment(birthDate);
      const today = moment();
      return birthDateFormatted.isValid() && today.diff(birthDateFormatted, 'years') > ADULT_AGE;
    },

    updateGuestField: (guestIndex, key, value) => {
      const report = JSON.parse(JSON.stringify(state.report));
      const guest = report.guests[guestIndex];
      guest[key] = value;
      if (key === FIELDS.DOCUMENTSUPPORT_KEY) {
        guest.document_support_number = value;
        guest.supportDocument = value;
      } else if (key === FIELDS.CONTACTTYPE_KEY) {
        if (value == 'email') guest.phone = null;
        else if (value == 'telefono') guest.email = null;
        guest.contactValue = null;
      }

      if (key === FIELDS.ZIPCODE_KEY) {
        const country = guest?.country || 'ESP';
        const errors = { ...state.errorFields };
        const guestErrors = [...errors.guests[guestIndex]];

        const existingIndex = guestErrors.indexOf(FIELDS.ZIPCODE_KEY);
        if (existingIndex !== -1) guestErrors.splice(existingIndex, 1);

        // if (!isValidPostalCode(value, country)) {
        //   guestErrors.push(FIELDS.ZIPCODE_KEY);
        // }

        errors.guests[guestIndex] = guestErrors;
        updateState({ errorFields: errors });
      }

      // if (key === 'contactType') {
      //   if (value != initialReport?.guests?.[guestIndex]?.contactType) {
      //     guest.contactValue = '';
      //   } else {
      //     if (guest.contactValue === '') {
      //       guest.contactValue = initialReport?.guests?.[guestIndex]?.contactValue;
      //     }
      //   }
      // }

      updateState({ report });
    },

    validateZipCode: (guestIndex, zipCode) => {
      const guest = state.report.guests[guestIndex];
      const country = guest?.country || 'ESP';
      const errors = { ...state.errorFields };
      const guestErrors = [...errors.guests[guestIndex]];

      const existingIndex = guestErrors.indexOf(FIELDS.ZIPCODE_KEY);
      if (existingIndex !== -1) guestErrors.splice(existingIndex, 1);

      // if (!isValidPostalCode(zipCode, country)) {
      //   guestErrors.push(FIELDS.ZIPCODE_KEY);
      // }

      errors.guests[guestIndex] = guestErrors;
      updateState({ errorFields: errors });
    },

    updateContractField: (key, value) => {
      const report = JSON.parse(JSON.stringify(state.report));
      report.contract[key] = value;
      updateState({ report });
    },

    onBackClicked: () => {
      navigate(-1);
    },
    disableSupportNumber: docType => {
      if (!docType) return false;
      if (docType === 'dni' || docType === 'residencePermit') return false;

      return true;
    },
    isDocTypeEmpty: (docType, index) => {
      // doctype set to "empty" when is null or has no data for better handling
      if (!docType) {
        actions.updateGuestField(index, FIELDS.DOCUMENTTYPE_KEY, 'empty');
      }
    },
    disableFieldOnDocTypeEmpty(documentType) {
      if (documentType === 'empty') {
        return true;
      }
      return false;
    },

    sendWithEmptyDocument: guest => {
      // determines if u can send an empty document (only childs)
      const isAdult = actions.isAdult(guest[FIELDS.BIRTHDATE_KEY]);
      if (guest?.documentType === 'empty' && !isAdult) return true;
      return false;
    },

    isAdult: birthDate => {
      if (!birthDate) return true; // if no date, assume adult

      const birth = new Date(birthDate);
      const today = new Date();

      let age = today.getFullYear() - birth.getFullYear();

      // birthdate month/day check
      const hasBirthdayPassed =
        today.getMonth() > birth.getMonth() ||
        (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());

      if (!hasBirthdayPassed) {
        age--;
      }

      return age >= 14;
    },
    sendDeleteBooking: () => {
      updateState({ isLoading: true });
      const bookingId = state.bookingId;
      const date = state.report.contract?.entrance;

      const data = {
        bookingId, // uuid
        date,
        hotelId: getSelectedHotel().key,
      };
      API.policeReportSendDelete(data)
        .then(res => {
          notification.success({
            message: translate('policeReport.delete.success'),
          });
          updateState({ isLoading: false, showBookingDeleteWarning: false });
          navigate(-1);
        })
        .catch(err => {
          notification.error({
            message: translate('policeReport.delete.error'),
          });
          updateState({ isLoading: false, showBookingDeleteWarning: false });
        });
    },
    sendDeleteGuest: () => {
      updateState({ isLoading: true });

      const guestId = state.guestToDelete.id;
      const date = state.report.contract?.entrance;

      const data = {
        guestId,
        date,
        hotelId: getSelectedHotel().key,
      };

      API.policeReportSendDelete(data)
        .then(res => {
          notification.success({
            message: translate('policeReport.delete.success'),
          });

          // Delete the guest for front-End
          const updatedGuests = state.report.guests.filter(guest => guest.id !== guestId);
          const updatedReport = { ...state.report, guests: updatedGuests };

          updateState({
            isLoading: false,
            showGuestDeleteWarning: false,
            report: updatedReport,
          });

          window.location.reload(); // reloag page
        })
        .catch(err => {
          notification.error({
            message: translate('policeReport.delete.error'),
          });
          updateState({ isLoading: false, showGuestDeleteWarning: false });
        });
    },

    validateDocumentNumberRepeated: () => {
      const guests = state.report.guests;
      const documentMap = {};
      const repeatedIndexes = [];

      guests.forEach((guest, index) => {
        const docNumber = guest?.[FIELDS.DOCUMENTNUMBER_KEY]?.trim();
        if (!docNumber) return;

        if (!documentMap[docNumber]) {
          documentMap[docNumber] = [];
        }
        documentMap[docNumber].push(index);
      });

      Object.values(documentMap).forEach(indices => {
        if (indices.length > 1) {
          repeatedIndexes.push(...indices);
        }
      });

      return repeatedIndexes.length > 0 ? repeatedIndexes : false;
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}
